import { useCallback } from 'react';
import {
    normalizePriceRefreshStatusPayload,
} from '../dashboardUtils';

export default function usePriceRefreshStatus({
    userUid,
    readPriceRefreshStatusRaw,
    autoRefreshEnabledRef,
    lastAppliedPriceWatermarkRef,
    lastPendingPriceWatermarkRef,
    priceRefreshStatusRequestIdRef,
    setPriceRefreshStatus,
    setPendingAutoRefresh,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
}) {
    const fetchPriceRefreshStatus = useCallback(async ({
        markApplied = false,
        silent = false,
        sinceUpdatedAt = lastAppliedPriceWatermarkRef.current,
    } = {}) => {
        if (!userUid) {
            if (markApplied) {
                lastAppliedPriceWatermarkRef.current = null;
                lastPendingPriceWatermarkRef.current = null;
                setPendingAutoRefresh(null);
            }
            setPriceRefreshStatus(null);
            return null;
        }

        const requestId = ++priceRefreshStatusRequestIdRef.current;

        const priceRefreshRead = await readPriceRefreshStatusRaw({
            silent,
            sinceUpdatedAt,
        });
        if (priceRefreshStatusRequestIdRef.current !== requestId) {
            return null;
        }
        if (!priceRefreshRead.ok || !priceRefreshRead.status) {
            return null;
        }

        const normalized = normalizePriceRefreshStatusPayload(priceRefreshRead.status, {
            hasNewerPrices: markApplied ? false : priceRefreshRead.status?.has_newer_prices,
        });

        setPriceRefreshStatus(normalized);

        if (markApplied) {
            lastAppliedPriceWatermarkRef.current = normalized.latest_security_updated_at;
            lastPendingPriceWatermarkRef.current = null;
            setPendingAutoRefresh(null);
        }

        return normalized;
    }, [
        lastAppliedPriceWatermarkRef,
        lastPendingPriceWatermarkRef,
        priceRefreshStatusRequestIdRef,
        readPriceRefreshStatusRaw,
        setPendingAutoRefresh,
        setPriceRefreshStatus,
        userUid,
    ]);

    const notifyPendingPriceRefresh = useCallback((status) => {
        if (!status?.latest_security_updated_at) return;
        if (lastPendingPriceWatermarkRef.current === status.latest_security_updated_at) return;

        lastPendingPriceWatermarkRef.current = status.latest_security_updated_at;
        const changedCount = Number(status?.updated_security_count_since || 0);
        const changedLabel = changedCount > 0
            ? ` for ${changedCount} securit${changedCount === 1 ? 'y' : 'ies'}`
            : '';
        setSnackbarMessage(`New stored prices are ready${changedLabel}. Sync when you're ready.`);
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
    }, [
        lastPendingPriceWatermarkRef,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
    ]);

    const markPendingRefreshFromLoadResult = useCallback((loadResult) => {
        if (!loadResult?.watermarkAdvancedDuringLoad || !loadResult?.priceRefreshStatus) {
            return false;
        }

        setPendingAutoRefresh(loadResult.priceRefreshStatus);

        if (!autoRefreshEnabledRef.current) {
            notifyPendingPriceRefresh(loadResult.priceRefreshStatus);
        }

        return true;
    }, [autoRefreshEnabledRef, notifyPendingPriceRefresh, setPendingAutoRefresh]);

    return {
        fetchPriceRefreshStatus,
        notifyPendingPriceRefresh,
        markPendingRefreshFromLoadResult,
    };
}
